<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { requiredIf, helpers } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

import Button from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ExecuteCardAutomationFormField from './ExecuteCardAutomationFormField.vue';

const props = defineProps({
  automation: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['confirm']);
const { t } = useI18n();

const formState = ref({});
const formRules = {};

const parameterFields = computed(() => {
  return props.automation.parameters || [];
});

// Custom validator for array fields - checks that all items have a value
const requiredArray = isRequired =>
  helpers.withParams({ type: 'requiredArray' }, value => {
    if (!isRequired) return true;
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(item => item.value !== '' && item.value !== undefined)
    );
  });

// Custom validator for number fields
const validNumber = fieldType =>
  helpers.withParams({ type: 'validNumber' }, value => {
    if (fieldType !== 'number') return true;
    const val = value?.value;
    if (val === null || val === '' || val === undefined) return true;
    return !Number.isNaN(Number(val));
  });

// Custom validator for array of numbers
const validNumberArray = fieldType =>
  helpers.withParams({ type: 'validNumberArray' }, value => {
    if (fieldType !== 'number') return true;
    if (!Array.isArray(value)) return true;
    return value.every(
      item =>
        item.value === null ||
        item.value === '' ||
        item.value === undefined ||
        !Number.isNaN(Number(item.value))
    );
  });

parameterFields.value.forEach(field => {
  const defaultValue = field.type === 'boolean' ? false : '';
  let initialValue;
  if (field.is_array) {
    initialValue = field.required ? [{ value: defaultValue }] : [];
  } else {
    initialValue = { value: defaultValue };
  }
  formState.value[field.variable_name] = initialValue;

  const isRequired = field.type !== 'boolean' && field.required;

  if (field.is_array) {
    formRules[field.variable_name] = {
      requiredArray: requiredArray(isRequired),
      validNumberArray: validNumberArray(field.type),
    };
  } else {
    formRules[field.variable_name] = {
      required: requiredIf(() => isRequired),
      validNumber: validNumber(field.type),
    };
  }
});

const castParameterValue = (value, type, isArray = false) => {
  if (isArray) {
    return value
      .map(v => castParameterValue(v, type))
      .filter(v => v !== null && v !== '');
  }

  if (type === 'number') {
    if (value === '') return null;
    return Number(value);
  }
  if (type === 'boolean') {
    return !!value;
  }
  return value === null || value === undefined ? null : `${value}`;
};

const state = computed(() => {
  const data = {};
  parameterFields.value.forEach(field => {
    const type = field.type;

    if (field.is_array) {
      const value = formState.value[field.variable_name].map(
        item => item.value
      );
      data[field.variable_name] = castParameterValue(value, type, true);
      return;
    }

    const value = formState.value[field.variable_name].value;
    data[field.variable_name] = castParameterValue(value, type);
  });
  return data;
});

const v$ = useVuelidate(formRules, formState);

const getFieldLabel = field => {
  return field.label + (field.required ? ' *' : '');
};

const addArrayItem = field => {
  const defaultValue = field.type === 'boolean' ? false : '';
  formState.value[field.variable_name].push({ value: defaultValue });
};

const removeArrayItem = (field, index) => {
  formState.value[field.variable_name].splice(index, 1);
};

const updateArrayItem = (field, index, value) => {
  formState.value[field.variable_name][index] = value;
};

const onConfirm = () => {
  emit('confirm');
};

defineExpose({ state, v$ });
</script>

<template>
  <form class="space-y-4" @submit.prevent="onConfirm">
    <div
      v-for="field of parameterFields"
      :key="field.variable_name"
      class="form-field"
    >
      <!-- Array Field -->
      <div v-if="field.is_array" class="space-y-2">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12 flex gap-1 items-center relative"
        >
          <span>{{ getFieldLabel(field) }}</span>
          <div
            v-if="field.description"
            v-tooltip.right="{
              content: field.description,
              container: false,
            }"
            class="flex items-center justify-center flex-shrink-0 rounded-full hover:bg-n-alpha-2 size-4"
          >
            <Icon
              icon="i-lucide-info"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
        </label>
        <div
          v-for="(item, index) in formState[field.variable_name]"
          :key="index"
          class="flex gap-2 items-center"
        >
          <div class="flex-1">
            <ExecuteCardAutomationFormField
              :model-value="item"
              :type="field.type"
              label=""
              description=""
              :required="false"
              :rules="{}"
              @update:model-value="v => updateArrayItem(field, index, v)"
            />
          </div>
          <Button
            size="xs"
            color="ruby"
            variant="ghost"
            icon="i-lucide-trash-2"
            class="mt-0.5"
            @click.prevent="removeArrayItem(field, index)"
          />
        </div>
        <Button
          size="sm"
          color="slate"
          variant="faded"
          icon="i-lucide-plus"
          :label="
            t(
              'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.SIDEBAR.AUTOMATION.CONFIRM.ADD_ITEM_LABEL'
            )
          "
          @click.prevent="addArrayItem(field)"
        />
        <span
          v-if="v$[field.variable_name]?.$error"
          class="block min-w-0 mt-1 mb-0 text-xs truncate text-n-ruby-9 dark:text-n-ruby-9"
        >
          {{
            t(
              'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.SIDEBAR.AUTOMATION.FORM.ERRORS.REQUIRED'
            )
          }}
        </span>
      </div>

      <!-- Single Field -->
      <ExecuteCardAutomationFormField
        v-else
        v-model="formState[field.variable_name]"
        :type="field.type"
        :label="field.label"
        :description="field.description"
        :required="field.required == true"
        :rules="formRules[field.variable_name]"
      />
    </div>
  </form>
</template>
